import React, { useEffect } from "react";
import {
  CircularProgress,
  FormControlLabel,
  Switch,
} from "@mui/material";

import LinkDisplay from "components/Base/LinkDisplay";
import GenericRecordSummary from "components/Records/RecordDisplay/GenericRecordSummary";
import FulfillmentDetails from "components/Interactions/GenericDetailPage/FulfilmentDetails";
import ContentDetails, {
  FieldConfig,
} from "components/Interactions/GenericDetailPage/ContentDetails";
import BasePaper from "components/Base/BasePaper";
import GenericTabs, {
  GenericTabItem,
} from "components/Base/GenericTabs";

import OrderDetails from "./OrderDetails";
import { campaignAPI } from "api/Campaigns/CampaignAPI";

import RegistrationTab from "components/Interactions/GenericDetailPage/RegistrationTab";
import LoggingTab from "components/Interactions/GenericDetailPage/LoggingTab";

interface GenericDetailPageProps<T> {
  record: T | null;
  loading: boolean;
  saving: boolean;
  onClose?: () => void;
  onUpdate: (
    id: number,
    field: keyof T,
    value: any
  ) => Promise<void>;
  onToggleFulfilled: () => void;
  contentDetailFields: FieldConfig[];
  fulfilled: boolean;
  fulfillmentDetailsLabel: string;
  numberOfItems: number;
  costPerItem: number;
  showSaveButton?: boolean;
  onSave?: () => void;
  saveButtonColor?: string;

  fulfilledByPathwaysAvailable?: boolean;

  registration?: boolean;
  registrationLink?: string;

  logging?: boolean;
  loggingLink?: string;
  loggingLabel?: string;
}

function GenericDetailPage<
  T extends {
    id: number;
    name?: string;
    description?: string | null;
  }
>({
  record,
  loading,
  saving,
  onClose,
  onUpdate,
  onToggleFulfilled,
  contentDetailFields,
  fulfilled,
  fulfillmentDetailsLabel,
  numberOfItems,
  costPerItem,
  showSaveButton,
  onSave,
  saveButtonColor,
  fulfilledByPathwaysAvailable = false,
  registration = false,
  registrationLink,
  logging = false,
  loggingLink,
  loggingLabel = "Attendance",
}: GenericDetailPageProps<T>) {
  const [tabValue, setTabValue] = React.useState(0);
  const [linkedCampaign, setLinkedCampaign] =
    React.useState<any>(null);

  useEffect(() => {
    const id = (record as any)?.linked_campaign;

    if (!id) {
      setLinkedCampaign(null);
      return;
    }

    let active = true;

    campaignAPI
      .getById(id)
      .then((data) => {
        if (active) {
          setLinkedCampaign(data);
        }
      })
      .catch(() => {
        if (active) {
          setLinkedCampaign(null);
        }
      });

    return () => {
      active = false;
    };
  }, [record]);

  if (loading) {
    return <CircularProgress />;
  }

  if (!record) {
    return null;
  }

  const tabs: GenericTabItem[] = [
    {
      id: "content",
      label: "Content",
      content: (
        <ContentDetails
          fields={contentDetailFields}
          onChange={(field, value) =>
            onUpdate(
              record.id,
              field as keyof T,
              value
            )
          }
          onSave={onSave}
          saving={saving}
          showSaveButton={showSaveButton}
          saveButtonColor={saveButtonColor}
        />
      ),
    },
  ];

  if (fulfilledByPathwaysAvailable) {
    tabs.push(
      {
        id: "fulfilment",
        label: "Fulfilment",
        content: (
          <FulfillmentDetails
            fulfilled={fulfilled}
            itemsLabel={fulfillmentDetailsLabel}
            numberOfItems={numberOfItems}
            costPerItem={costPerItem}
            fulfilledByPathwaysAvailable={
              fulfilledByPathwaysAvailable
            }
            onSave={(field, value) =>
              console.log(
                "Updated",
                field,
                value
              )
            }
          />
        ),
      },
      {
        id: "orders",
        label: "Orders",
        disabled: !fulfilled,
        content: <OrderDetails />,
      }
    );
  }

  if (registration) {
    tabs.push({
      id: "registration",
      label: "Registration",
      content: (
        <RegistrationTab
          registrationLink={registrationLink}
        />
      ),
    });
  }

  if (logging) {
    tabs.push({
      id: "logging",
      label: loggingLabel,
      content: (
        <LoggingTab
          loggingLink={loggingLink}
          loggingLabel={loggingLabel}
        />
      ),
    });
  }

  return (
    <>
      <GenericRecordSummary
        record={record}
        titleField="name"
        descriptionField="description"
        onUpdate={onUpdate}
        loading={saving}
        onClose={onClose}
        rightContent={
          <>
            {(record as any).linked_campaign &&
              linkedCampaign && (
                <LinkDisplay
                  label={linkedCampaign.name}
                  url={`/campaigns/${linkedCampaign.id}`}
                  openUrl={`/campaigns/${linkedCampaign.id}`}
                  openLabel="Campaign"
                  showCopyButton={false}
                  showText
                  showOpenButton
                  withBackground={false}
                  sx={{
                    width: "auto",
                  }}
                />
              )}

            {fulfilledByPathwaysAvailable && (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!fulfilled}
                    onChange={onToggleFulfilled}
                    color="primary"
                    disabled={saving}
                  />
                }
                label="Fulfilled by Pathways"
              />
            )}
          </>
        }
      />

      <BasePaper>
        <GenericTabs
          tabs={tabs}
          value={tabValue}
          onChange={setTabValue}
          keepMounted
        />
      </BasePaper>
    </>
  );
}

export default GenericDetailPage;